import React, { useState, useEffect } from 'react';
import TeamChat from '../../hackathon/Chat';
import {
    collection,
    query,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    where,
    onSnapshot,
    addDoc
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    UserIcon,
    ArrowLeftIcon,
    UserAddIcon,
    CheckIcon,
    XIcon
} from '@heroicons/react/outline';
import { useAuth } from "../../../../Contexts/AuthContext";

const TeamDetails = () => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { userData } = useAuth();
    const [team, setTeam] = useState(location.state?.team || null);
    const [mentors, setMentors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mentorsLoading, setMentorsLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showMentorSelection, setShowMentorSelection] = useState(false);
    const [showChat, setShowChat] = useState(false);

    useEffect(() => {
        if (team) {
            // If team data is available from navigation, set up real-time listener
            setLoading(false);
            const teamRef = doc(db, 'hackathonTeams', teamId);
            const unsubscribe = onSnapshot(teamRef, (doc) => {
                if (doc.exists()) {
                    setTeam({ id: doc.id, ...doc.data() });
                }
            });

            return () => unsubscribe();
        } else {
            fetchTeamDetails();
        }
    }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchMentors();
    }, []);

    const fetchTeamDetails = async () => {
        try {
            setLoading(true);
            const teamRef = doc(db, 'hackathonTeams', teamId);
            const teamSnap = await getDoc(teamRef);

            if (teamSnap.exists()) {
                setTeam({ id: teamSnap.id, ...teamSnap.data() });
            } else {
                console.error('Team not found');
                navigate('/eventdashboard/teams-management');
            }
        } catch (error) {
            console.error('Error fetching team details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMentors = async () => {
        try {
            setMentorsLoading(true);
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('role', '==', 'Mentor'));
            const querySnapshot = await getDocs(q);

            const mentorsData = [];
            querySnapshot.forEach((doc) => {
                mentorsData.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            setMentors(mentorsData);
            console.log(mentorsData);
            setMentorsLoading(false);
        } catch (error) {
            console.error('Error fetching mentors:', error);
        } finally {
            setMentorsLoading(false);
        }
    };

    const assignMentor = async (mentor) => {
        try {
            setUpdating(true);
            const teamRef = doc(db, 'hackathonTeams', teamId);

            const mentorData = {
                id: mentor.id,
                name: mentor.firstName && mentor.lastName
                    ? `${mentor.firstName} ${mentor.lastName}`
                    : mentor.displayName || mentor.email,
                email: mentor.email,
                phoneNumber: mentor.phoneNumber || '',
                photoURL: mentor.photoURL || '',
                skills: mentor.skills || [],
                assignedAt: new Date()
            };

            // Create notification promises for all team members
            const notificationPromises = team.members?.map(member => {
                const notificationData = {
                    message: `Great news! ${mentorData.name} has been assigned as your team mentor. They will be available to guide and support your team throughout the hackathon.`,
                    read: false,
                    timestamp: new Date(),
                    userId: member.id
                };

                return addDoc(collection(db, 'notifications'), notificationData);
            }) || [];

            // Update team document
            const updateTeamPromise = updateDoc(teamRef, {
                mentorId: mentor.id,
                mentor: mentorData
            });

            // Execute all operations in parallel
            await Promise.all([
                updateTeamPromise,
                ...notificationPromises
            ]);

            // Update local state
            setTeam(prev => ({
                ...prev,
                mentorId: mentor.id,
                mentor: mentorData
            }));

            setSuccessMessage(`Mentor assigned successfully! Notifications sent to ${team.members?.length || 0} team members.`);
            setShowMentorSelection(false);
            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (error) {
            console.error('Error assigning mentor:', error);
        } finally {
            setUpdating(false);
        }
    };

    const removeMentor = async () => {
        try {
            setUpdating(true);
            const teamRef = doc(db, 'hackathonTeams', teamId);
            const mentorName = team.mentor?.name || 'Your mentor';

            // Create notification promises for all team members
            const notificationPromises = team.members?.map(member => {
                const notificationData = {
                    message: `${mentorName} has been unassigned from your team. A new mentor may be assigned shortly. Please continue working on your project.`,
                    read: false,
                    timestamp: new Date(),
                    userId: member.id
                };

                return addDoc(collection(db, 'notifications'), notificationData);
            }) || [];

            // Update team document
            const updateTeamPromise = updateDoc(teamRef, {
                mentorId: null,
                mentor: null
            });

            // Execute all operations in parallel
            await Promise.all([
                updateTeamPromise,
                ...notificationPromises
            ]);

            // Update local state
            setTeam(prev => ({
                ...prev,
                mentorId: null,
                mentor: null
            }));

            setSuccessMessage(`Mentor removed successfully! Notifications sent to ${team.members?.length || 0} team members.`);
            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (error) {
            console.error('Error removing mentor:', error);
        } finally {
            setUpdating(false);
        }
    };

    // Get team leader from members array using createdBy
    const getTeamLeader = () => {
        if (!team?.createdBy || !team?.members) return null;
        return team.members.find(member => member.id === team.createdBy);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1"></div>
            </div>
        );
    }

    if (!team) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Team Not Found</h1>
                    <button
                        onClick={() => navigate('/eventdashboard/teams-management')}
                        className="px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-opacity-90"
                    >
                        Back to Teams
                    </button>
                </div>
            </div>
        );
    }

    // Render the TeamChat inline as a section when showChat is true (no full-page takeover)

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-6">
                <button
                    onClick={() => navigate('/eventdashboard/teams-management')}
                    className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
                >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Back to Teams
                </button>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Team Details</h1>
                <p className="text-gray-600">Manage team information and assign mentors</p>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex">
                        <CheckIcon className="h-5 w-5 text-green-400" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-green-800">
                                {successMessage}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Team Information */}
                <div className="lg:col-span-2">
                    <div className="bg-white shadow rounded-lg p-6 mb-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">Team Information</h2>
                            <button
                                className="px-4 py-2 bg-custom-blue-1 text-white rounded hover:bg-opacity-90 text-sm font-semibold"
                                onClick={() => setShowChat(true)}
                            >
                                View Team Chat
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Team Name</label>
                                <p className="mt-1 text-sm text-gray-900">{team.name}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Description</label>
                                <p className="mt-1 text-sm text-gray-900">{team.description}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500">Team Leader</label>
                                {(() => {
                                    const teamLeader = getTeamLeader();
                                    return teamLeader ? (
                                        <div className="mt-1 flex items-center">
                                            <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center mr-3">
                                                {teamLeader.photoURL ? (
                                                    <img
                                                        src={teamLeader.photoURL}
                                                        alt={teamLeader.name}
                                                        className="h-8 w-8 rounded-full"
                                                    />
                                                ) : (
                                                    <UserIcon className="h-5 w-5 text-gray-600" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{teamLeader.name}</p>
                                                <p className="text-xs text-gray-500">{teamLeader.email}</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="mt-1 text-sm text-gray-500">No team leader assigned</p>
                                    );
                                })()}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Members</label>
                                    <p className="mt-1 text-sm text-gray-900">{team.members?.length || 0} members</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Created</label>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {team.createdAt
                                            ? new Date(
                                                team.createdAt.seconds
                                                    ? team.createdAt.seconds * 1000
                                                    : team.createdAt
                                            ).toLocaleDateString()
                                            : 'Unknown'}

                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Inline Chat Section */}
                    {showChat && (
                        <div className="bg-white shadow rounded-lg p-6 mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Team Chat</h3>
                                <button className="text-sm text-gray-500" onClick={() => setShowChat(false)}>Close</button>
                            </div>
                            <div className="h-[60vh] overflow-hidden">
                                <TeamChat teamId={team.id} teamData={team} onBack={() => setShowChat(false)} />
                            </div>
                        </div>
                    )}

                    {/* Team Members */}
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Team Members</h2>
                        <div className="space-y-3">
                            {team.members?.map((member, index) => (
                                <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                    <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                        {member.photoURL ? (
                                            <img
                                                src={member.photoURL}
                                                alt={member.name}
                                                className="h-10 w-10 rounded-full"
                                            />
                                        ) : (
                                            <UserIcon className="h-6 w-6 text-gray-600" />
                                        )}
                                    </div>
                                    <div className="ml-3 flex-1">
                                        <p className="text-sm font-medium text-gray-900">{member.name}</p>
                                        <p className="text-sm text-gray-500">{member.email}</p>
                                        {member.skills && member.skills.length > 0 && (
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                {member.skills.slice(0, 3).map((skill, skillIndex) => (
                                                    <span
                                                        key={skillIndex}
                                                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                                    >
                                                        {skill}
                                                    </span>
                                                ))}
                                                {member.skills.length > 3 && (
                                                    <span className="text-xs text-gray-500">
                                                        +{member.skills.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {team.createdBy === member.id && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            Team Leader
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Mentor Assignment */}
                <div className="lg:col-span-1">
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Mentor Assignment</h2>

                        {team.mentor ? (
                            <div className="space-y-4">
                                <div className="p-4 bg-green-50 rounded-lg">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-medium text-green-800">Assigned Mentor</h3>
                                        {userData?.role === 'Administrator' && (
                                            <button
                                                onClick={removeMentor}
                                                disabled={updating}
                                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                            >
                                                <XIcon className="h-4 w-4" />
                                            </button>)}
                                    </div>
                                    <div className="flex items-center">
                                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                            {team.mentor.photoURL ? (
                                                <img
                                                    src={team.mentor.photoURL}
                                                    alt={team.mentor.name}
                                                    className="h-10 w-10 rounded-full"
                                                />
                                            ) : (
                                                <UserIcon className="h-6 w-6 text-gray-600" />
                                            )}
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm font-medium text-gray-900">{team.mentor.name}</p>
                                            <p className="text-sm text-gray-500">{team.mentor.email}</p>
                                            {team.mentor.assignedAt && (
                                                <p className="text-xs text-gray-400">
                                                    Assigned: {formatDate(team.mentor.assignedAt)}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {team.mentor.skills && team.mentor.skills.length > 0 && (
                                        <div className="mt-3 flex flex-wrap gap-1">
                                            {team.mentor.skills.slice(0, 3).map((skill, index) => (
                                                <span
                                                    key={index}
                                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                                                >
                                                    {skill}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {userData?.role === 'Administrator' && (
                                    <button
                                        onClick={() => setShowMentorSelection(true)}
                                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        Change Mentor
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="text-center">
                                <div className="p-4 bg-gray-50 rounded-lg mb-4">
                                    <UserAddIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">No mentor assigned to this team</p>
                                </div>
                                {userData?.role === 'Administrator' && (
                                    <button
                                        onClick={() => setShowMentorSelection(true)}
                                        className="w-full px-4 py-2 bg-custom-green-1 text-white rounded-lg hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-green-500"
                                    >
                                        Assign Mentor
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Mentor Selection Modal */}
                        {showMentorSelection && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                                    <div className="mt-3">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-medium text-gray-900">Select Mentor</h3>
                                            <button
                                                onClick={() => setShowMentorSelection(false)}
                                                className="text-gray-400 hover:text-gray-600"
                                            >
                                                <XIcon className="h-6 w-6" />
                                            </button>
                                        </div>

                                        {mentorsLoading ? (
                                            <div className="flex justify-center py-4">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-blue-1"></div>
                                            </div>
                                        ) : (
                                            <div className="max-h-64 overflow-y-auto space-y-2">
                                                {mentors.map((mentor) => (
                                                    <div
                                                        key={mentor.id}
                                                        className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                                                        onClick={() => assignMentor(mentor)}
                                                    >
                                                        <div className="flex items-center">
                                                            <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                                                                {mentor.photoURL ? (
                                                                    <img
                                                                        src={mentor.photoURL}
                                                                        alt={mentor.firstName || mentor.displayName}
                                                                        className="h-8 w-8 rounded-full"
                                                                    />
                                                                ) : (
                                                                    <UserIcon className="h-5 w-5 text-gray-600" />
                                                                )}
                                                            </div>
                                                            <div className="ml-3">
                                                                <p className="text-sm font-medium text-gray-900">
                                                                    {mentor.firstName && mentor.lastName
                                                                        ? `${mentor.firstName} ${mentor.lastName}`
                                                                        : mentor.displayName || mentor.email}
                                                                </p>
                                                                <p className="text-xs text-gray-500">{mentor.email}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {mentors.length === 0 && (
                                                    <p className="text-center text-gray-500 py-4">No mentors available</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamDetails;
